import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { select, Store } from "@ngrx/store";
import { combineLatest, Observable, Subscription } from "rxjs";
import { take } from "rxjs/operators";
import { filterNullOrUndefined } from 'src/app/@operators/filter-null-or-undefined';
import { AccessType, HospitalizationDto, PrehospiCreationDto, QuestionnaireApplicableRulesDto } from "src/app/api/models";
import {  eKioskError, ePrintingStatus } from 'src/app/shared/models/api/constants';
import { AppState, getState, resetStates } from "src/app/state/app.state";
import { environment } from 'src/environments/environment';
import { CheckPrintingStatus, ICprintDocument } from "../../../print-monitor";
import * as ArcHospiActions from '../../archospi/state/archospi.actions';
import { setErrorMsg } from '../../error/state/error.actions';
import { createPrehospi, getMedicalForm, getPrintLight, sendMedicalForm } from '../../hospi/state/hospi.actions';
import { setTitle } from '../../title/state/title.actions';
import { getAnswerLightInfo, getHospitalisationRegistered, getPrintLightPDF, getMedicalFormPDF, getHospitalizationRegisterError } from "../../transaction-info/state/transaction-info.selectors";
import { SendMedicalFormComponent } from "../send-medical-form/send-medical-form.component";
import { MatDialog } from "@angular/material/dialog";
import { getToken } from "../../authentication/state/authentication.selectors";
import { TokenResponseModel } from "src/app/authenticator/models";

@Component({
    selector: "app-light",
    templateUrl: "./light.component.html",
    styleUrls: ["./light.component.scss"],
    standalone: false
})
export class LightComponent implements OnInit,OnDestroy {
	public light$: Observable<QuestionnaireApplicableRulesDto> = this.store.pipe(select(getAnswerLightInfo));
	public kioskMode: boolean = environment.kioskMode;
	private subs: Subscription[] = [];
	public lightColor: string;
	public message: string;
	public state: AppState;
	public medicalFormRequired: boolean = undefined;
	public printUsingPdf = true;
	public hospitalization: HospitalizationDto= undefined;
	public medFormString: string;
	private stopProcessInterval: any;
	private printManagementInterval: any;
	public printMedicalFormEnabled = true;
	public printLightEnabled = true;
	private nbPrintManagement = 0;
	public isPrinting = false;
	public nbPrinting = 0;
	public printingError = false;
	@ViewChild('medFormContent') medFormContent: ElementRef;
	public nbSendMailMedicalForm: number = 0;
	public nbMaxSendMailMedicalForm: number = 2;
	public mailMedicalFormSend: boolean = false;
	private tokenSaved: TokenResponseModel; 
  private accessToken: string | null = null;
	constructor(private store: Store<AppState>, public dialog: MatDialog) {
		this.store.dispatch(
			setTitle({
				data: "TITLE_HOSPITALIZATION_REGISTERED",
				data2: "",
			})
		);
		this.subs.push(getState(this.store).subscribe(state =>{
			this.state = state;
		}));

		this.subs.push(getState(this.store).pipe(take(1)).subscribe(state =>
		{
			this.printLightEnabled = state.kiosk.printLightEnabled;
			this.nbMaxSendMailMedicalForm = state.kiosk.maxNbSendMailMedicalForm;
			const prehospiCreationParam: PrehospiCreationDto =
				{
					admissionDate: state.transactionInfo.admissionDate.toISOString(),
					alreadyExistingPrehospi: state.transactionInfo.hospitalisation,
					answers: state.transactionInfo.answerLightInfo.answers.split('.').map(a => parseInt(a)),
					contractId: state.transactionInfo.selectedContract.contractId,
					hospitalNo: state.transactionInfo.selectedSite.hospitalNo,
					siteId: state.transactionInfo.selectedSite.siteId,
					transactionId: state.archospi.arcHospi.transactionId,
					light: state.transactionInfo.answerLightInfo.light,
					languageCode: state.transactionInfo.selectedLanguage.languageCode,
					questionnaireId: state.transactionInfo.questionnaire.questionnaireId,
					medFormName: state.transactionInfo.answerLightInfo.medFormName,
					isMedFormRequired: state.transactionInfo.answerLightInfo.medicalForm
				}
			this.store.dispatch(createPrehospi({ data: prehospiCreationParam }));
		}));
	}
	ngOnInit(): void {
		this.subs.push(
      combineLatest([
        this.store.pipe(select(getHospitalisationRegistered)),
        this.store.pipe(select(getToken))
      ]).pipe(filterNullOrUndefined())
			.subscribe(([hospitalizationRegistered, token]) => {
				if(!token){
					return 
				}
				this.tokenSaved = token;
				if (hospitalizationRegistered == null)
					return;
				if (hospitalizationRegistered) {
					this.hospitalization = this.state.transactionInfo.hospitalisation;

					//Set message
					this.message = this.state.transactionInfo.answerLightInfo.ruleMessageText;

					//Set light
					switch (this.hospitalization.light.toUpperCase()) {
						case "R":
							this.lightColor = "red";
							break;
						case "O":
							this.lightColor = "orange";
							break;
						default:
							this.lightColor = "green";
							break;
					}
					this.store.dispatch(ArcHospiActions.preHospiComplete({
						hospitalizationId: this.hospitalization.hospitalizationId, lightColor: this.hospitalization.light,
						medicalFormRequired: this.hospitalization.medicalForm, medicalFormName: this.hospitalization.medFormName,
						reasons: this.hospitalization.answers
					}));
					this.medicalFormRequired = this.hospitalization.medicalForm;

					//Print medical form
					if (this.medicalFormRequired)
						this.onPrintMedicalForm(true);
				}
				else {
					var error = this.state.transactionInfo.hospitalizationRegisterError;
					if (error) {
						this.store.dispatch(setErrorMsg({ data: null, KioskError: eKioskError[error], noTranslation: false }));
					}
					else
						this.store.dispatch(setErrorMsg({ data: null, KioskError: eKioskError.HOSPITALISATION_REGISTER_ERROR }));
				}
			}));

		this.resetStopProcessInterval();
	}

	@HostListener('document:click', ['$event'])
	documentClick(event: MouseEvent) {
		this.resetStopProcessInterval();
	}


	resetStopProcessInterval() {
		if (environment.kioskMode && window.external.EndProcess) {
			clearInterval(this.stopProcessInterval);
			this.stopProcessInterval = setInterval(function () {
				window.external.EndProcess();
			}, this.state.kiosk.stopProcessInterval * 1_000);
		}
	}

	onPrintMedicalForm(automatic: boolean) {
		var method = environment.kioskMode ? "GetMedicalFormPDF" : "GetMedicalFormHtml";
		
		const url = environment.apiBaseUrl + '/v1/hospitalization/' + method 
		+ '?insurerId=' + this.state.transactionInfo.selectedInsurer.insurerId 
		+ '&cardId=' + this.state.transactionInfo.cardId 
		+ '&languageCode=' + this.state.transactionInfo.selectedLanguage.languageCode 
		+ '&hospitalizationId=' + this.hospitalization.hospitalizationId;

    	const accessToken = this.tokenSaved.access_token;

		ICprintDocument(url, accessToken);
		this.printStatusManagement();

		if (!automatic && this.kioskMode) {
			//disable print button after once print
			this.printMedicalFormEnabled = false;
		}
	}

	onPrintLight() {
		if (!this.kioskMode) {
			this.store.dispatch(getPrintLight({
				languageCode: this.state.transactionInfo.selectedLanguage.languageCode,
				hospitalizationId: this.state.transactionInfo.hospitalisation.hospitalizationId,
				messageId: this.state.transactionInfo.answerLightInfo.messageId
			}));

			this.subs.push(this.store.pipe(select(getPrintLightPDF)).pipe(filterNullOrUndefined(), take(1)).subscribe(printLight => {
				
				const a = document.createElement('a');
				a.href = URL.createObjectURL(printLight);
				a.download = "printLight" + this.state.transactionInfo.hospitalisation.hospitalizationId + this.state.transactionInfo.selectedLanguage.languageCode;
				a.click();

			}));
		}
		else {
			this.printLightEnabled = false;

			const url = environment.apiBaseUrl + '/v1/hospitalization/GetPrintLightHTML?'
			+'insurerId=' + this.state.transactionInfo.selectedInsurer.insurerId 
			+'&cardId=' + this.state.transactionInfo.cardId 
			+'&hospitalizationId=' + this.hospitalization.hospitalizationId 
			+'&languageCode=' + this.state.transactionInfo.selectedLanguage.languageCode 
			+'&messageId=' + this.state.transactionInfo.answerLightInfo.messageId;

			const accessToken = this.tokenSaved.access_token;

			ICprintDocument(url,accessToken);
			this.printStatusManagement();
		}
	}
	ngOnDestroy(): void {
		this.subs.forEach((_) => _.unsubscribe());
	}
	onDownload() {
		this.store.dispatch(getMedicalForm({
			languageCode: this.state.transactionInfo.selectedLanguage.languageCode,
			hospitalizationId: this.state.transactionInfo.hospitalisation.hospitalizationId
		}));

		this.subs.push(this.store.pipe(select(getMedicalFormPDF)).pipe(filterNullOrUndefined(), take(1)).subscribe(medForm => {
			const a = document.createElement('a');
			a.href = URL.createObjectURL(medForm);
			a.download = "medForm" + this.state.transactionInfo.selectedInsurer.insurerId + this.state.transactionInfo.cardId;
			a.click();
		}));
	}

	onMail() {
		const dialogRef = this.dialog.open(SendMedicalFormComponent, {
			width: '600px',
			data: { mail: this.state.transactionInfo.selectedContract.email }
		});

		dialogRef.afterClosed().subscribe(result => {
			if (environment.kioskMode && window.external.HideKeyboard) {
				window.external.HideKeyboard();
			}
			if (result != undefined) {
				this.nbSendMailMedicalForm++;
				this.mailMedicalFormSend = true;
				this.store.dispatch(sendMedicalForm({
					hospitalizationId: this.state.transactionInfo.hospitalisation.hospitalizationId,
					languageCode: this.state.transactionInfo.selectedLanguage.languageCode,
					medicalForm: this.state.transactionInfo.hospitalisation.medFormName,
					recipient: result
				}));
			}
			else
				this.mailMedicalFormSend = false;
		});

	}

	onHome() {
		resetStates(this.store);
		if (environment.kioskMode ){
			if(window.external.EndProcess)
				window.external.EndProcess();
			if(window.external.ProcessComplete)
				window.external.ProcessComplete();
		}
	}

	printStatusManagement() {
		if (this.kioskMode && window.KioPrintControler) {
			this.nbPrinting++;
			this.isPrinting = true;
			this.printManagementInterval = setInterval(() => {
				this.nbPrintManagement++;
				const printStatus = CheckPrintingStatus();
				if (this.nbPrintManagement >= 15 || printStatus == ePrintingStatus.COMPLETE || printStatus == ePrintingStatus.ERROR) {
					if (printStatus == ePrintingStatus.ERROR)
						this.printingError = true;

					this.isPrinting = false;
					clearInterval(this.printManagementInterval);
				}

				this.resetStopProcessInterval();
			}, 2000);
		}
	}
}